import { getSintegradaServerApi } from "@/services/api";
import { ResumeGrouped } from "@/services/esus/attendanceService";

export type HospitalizationDashboardParams = {
	uf: string;
	ibge_code: string;
	start_date: string;
	end_date: string;
	cnes?: string;
	min_age: number | null;
	max_age: number | null;
	department_id: number;
	hospital_ward_id?: number;
};

export type PermanenceMetrics = {
	tempoMedioPermanencia: number;
	quantidadeMenor24h: number;
	quantidadeEntre24h48h: number;
	quantidadeEntre48h72h: number;
	quantidadeMaior72h: number;
	totalPermanencias: number;
};

export type OccupationRate = {
	totalOcupacoes: number;
	totalLeitos: number;
	diasPeriodo: number;
	taxaOcupacao: number;
};

export type PermanenceAndOccupationMetrics = {
	permanenceMetrics: PermanenceMetrics;
	occupationRate: OccupationRate;
};

export type HospitalizationDashboardData = {
	aggregatedByHealthUnitTransfer: ResumeGrouped<string, null>[];
	averageHospitalizationsTimeInSecondsByType: ResumeGrouped<
		"Observação" | "Internamento" | "Estabilização",
		null
	>[];
	aggregatedByTypeAndTimeClassification: ResumeGrouped<
		"Observação" | "Internamento" | "Estabilização",
		ResumeGrouped<string, object>
	>[];
	aggregatedByGender: ResumeGrouped<
		"Masculino" | "Feminino" | "Outros" | "Não informado",
		null
	>[];
	aggregatedObservationsByPeriodAndType: ResumeGrouped<
		string,
		ResumeGrouped<"Observação" | "Internamento" | "Estabilização", object>
	>[];
	totalBriefObservations: number;
	totalLongObservations: number;
	totalHospitalizations: number;
	totalStabilizations: number;
	totalPendingAdmittedObservations: number;
	totalPendingAdmittedHospitalizations: number;
	totalPendingAdmittedStabilizations: number;
	totalPendingNotAdmittedObservations: number;
	totalPendingNotAdmittedHospitalizations: number;
	totalPendingNotAdmittedStabilizations: number;
	totalDeaths: number;
	totalHospitalizationsAdmitted: number;
	totalHospitalizationsAdmittedByGender: ResumeGrouped<string, null>[];
	deathsReports: {
		totalDeaths: number;
		totalDeathsWithin24h: number;
		totalDeathsOver24h: number;
	};
	occupationReports: {
		totalOccupation: number;
		totalOccupationByGender: ResumeGrouped<string, null>[];
	};
	transfersReports: {
		totalTransfersByUnit: ResumeGrouped<string, null>[];
		totalTransfers: number;
	};
	permanenceAndOccupationMetrics: PermanenceAndOccupationMetrics;
};

export async function getHospitalizationDashboard({ ...params }: HospitalizationDashboardParams) {
	const url = `/attendance/hospitalization-dashboard`;

	const { data } = await getSintegradaServerApi().get<HospitalizationDashboardData>(url, params);

	// Garantir que todas as propriedades sejam arrays válidos
	const defaultData: HospitalizationDashboardData = {
		aggregatedByHealthUnitTransfer: [],
		averageHospitalizationsTimeInSecondsByType: [],
		aggregatedByTypeAndTimeClassification: [],
		aggregatedByGender: [],
		aggregatedObservationsByPeriodAndType: [],
		totalBriefObservations: 0,
		totalLongObservations: 0,
		totalHospitalizations: 0,
		totalStabilizations: 0,
		totalPendingAdmittedObservations: 0,
		totalPendingAdmittedHospitalizations: 0,
		totalPendingAdmittedStabilizations: 0,
		totalPendingNotAdmittedObservations: 0,
		totalPendingNotAdmittedHospitalizations: 0,
		totalPendingNotAdmittedStabilizations: 0,
		totalDeaths: 0,
		totalHospitalizationsAdmitted: 0,
		totalHospitalizationsAdmittedByGender: [],
		deathsReports: {
			totalDeaths: 0,
			totalDeathsWithin24h: 0,
			totalDeathsOver24h: 0,
		},
		occupationReports: {
			totalOccupation: 0,
			totalOccupationByGender: [],
		},
		transfersReports: {
			totalTransfersByUnit: [],
			totalTransfers: 0,
		},
		permanenceAndOccupationMetrics: {
			permanenceMetrics: {
				tempoMedioPermanencia: 0,
				quantidadeMenor24h: 0,
				quantidadeEntre24h48h: 0,
				quantidadeEntre48h72h: 0,
				quantidadeMaior72h: 0,
				totalPermanencias: 0,
			},
			occupationRate: {
				totalOcupacoes: 0,
				totalLeitos: 0,
				diasPeriodo: 0,
				taxaOcupacao: 0,
			},
		},
	};

	if (!data) {
		return defaultData;
	}

	// Validar e garantir que todas as propriedades de array sejam arrays válidos
	return {
		aggregatedByHealthUnitTransfer: Array.isArray(data.aggregatedByHealthUnitTransfer)
			? data.aggregatedByHealthUnitTransfer
			: [],
		averageHospitalizationsTimeInSecondsByType: Array.isArray(
			data.averageHospitalizationsTimeInSecondsByType
		)
			? data.averageHospitalizationsTimeInSecondsByType
			: [],
		aggregatedByTypeAndTimeClassification: Array.isArray(
			data.aggregatedByTypeAndTimeClassification
		)
			? data.aggregatedByTypeAndTimeClassification
			: [],
		aggregatedByGender: Array.isArray(data.aggregatedByGender) ? data.aggregatedByGender : [],
		aggregatedObservationsByPeriodAndType: Array.isArray(
			data.aggregatedObservationsByPeriodAndType
		)
			? data.aggregatedObservationsByPeriodAndType
			: [],
		totalBriefObservations: data.totalBriefObservations ?? 0,
		totalLongObservations: data.totalLongObservations ?? 0,
		totalHospitalizations: data.totalHospitalizations ?? 0,
		totalStabilizations: data.totalStabilizations ?? 0,
		totalPendingAdmittedObservations: data.totalPendingAdmittedObservations ?? 0,
		totalPendingAdmittedHospitalizations: data.totalPendingAdmittedHospitalizations ?? 0,
		totalPendingAdmittedStabilizations: data.totalPendingAdmittedStabilizations ?? 0,
		totalPendingNotAdmittedObservations: data.totalPendingNotAdmittedObservations ?? 0,
		totalPendingNotAdmittedHospitalizations: data.totalPendingNotAdmittedHospitalizations ?? 0,
		totalPendingNotAdmittedStabilizations: data.totalPendingNotAdmittedStabilizations ?? 0,
		totalDeaths: data.totalDeaths ?? 0,
		totalHospitalizationsAdmitted: data.totalHospitalizationsAdmitted ?? 0,
		totalHospitalizationsAdmittedByGender: Array.isArray(
			data.totalHospitalizationsAdmittedByGender
		)
			? data.totalHospitalizationsAdmittedByGender
			: [],
		deathsReports: {
			totalDeaths: data.deathsReports?.totalDeaths ?? 0,
			totalDeathsWithin24h: data.deathsReports?.totalDeathsWithin24h ?? 0,
			totalDeathsOver24h: data.deathsReports?.totalDeathsOver24h ?? 0,
		},
		occupationReports: {
			totalOccupation: data.occupationReports?.totalOccupation ?? 0,
			totalOccupationByGender: Array.isArray(data.occupationReports?.totalOccupationByGender)
				? data.occupationReports.totalOccupationByGender
				: [],
		},
		transfersReports: {
			totalTransfersByUnit: Array.isArray(data.transfersReports?.totalTransfersByUnit)
				? data.transfersReports.totalTransfersByUnit
				: [],
			totalTransfers: data.transfersReports?.totalTransfers ?? 0,
		},
		permanenceAndOccupationMetrics: {
			permanenceMetrics: {
				tempoMedioPermanencia:
					data.permanenceAndOccupationMetrics?.permanenceMetrics?.tempoMedioPermanencia ??
					0,
				quantidadeMenor24h:
					data.permanenceAndOccupationMetrics?.permanenceMetrics?.quantidadeMenor24h ?? 0,
				quantidadeEntre24h48h:
					data.permanenceAndOccupationMetrics?.permanenceMetrics?.quantidadeEntre24h48h ??
					0,
				quantidadeEntre48h72h:
					data.permanenceAndOccupationMetrics?.permanenceMetrics?.quantidadeEntre48h72h ??
					0,
				quantidadeMaior72h:
					data.permanenceAndOccupationMetrics?.permanenceMetrics?.quantidadeMaior72h ?? 0,
				totalPermanencias:
					data.permanenceAndOccupationMetrics?.permanenceMetrics?.totalPermanencias ?? 0,
			},
			occupationRate: {
				totalOcupacoes:
					data.permanenceAndOccupationMetrics?.occupationRate?.totalOcupacoes ?? 0,
				totalLeitos: data.permanenceAndOccupationMetrics?.occupationRate?.totalLeitos ?? 0,
				diasPeriodo: data.permanenceAndOccupationMetrics?.occupationRate?.diasPeriodo ?? 0,
				taxaOcupacao:
					data.permanenceAndOccupationMetrics?.occupationRate?.taxaOcupacao ?? 0,
			},
		},
	} as HospitalizationDashboardData;
}
